import type { ChartConfig, ChartData } from '@indutrust/chart-model';
import type { PieSeriesOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildSharedChrome,
  buildTooltipStyle,
  pickColor,
  resolvePalette,
} from './chart-option-chrome';
import { formatChartTooltipValue } from './format-chart-tooltip-value';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';
import type { SupportedChartConfiguration } from './supported-chart-configuration';

/**
 * Generate an ECharts configuration for a pie chart.
 *
 * Only the first dataset is plotted — one slice per label, colored from the palette.
 *
 * @param data The input series and labels. Uses the first dataset. {@link ChartData}
 * @param config Chart configuration (colors, title, etc.). {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} ready to feed `idt-chart-renderer`.
 * @example
 * const opt = createPieChartOptions(data, { type: 'pie' });
 */
export function createPieChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const palette = resolvePalette(config, theme);
  const hasDataset = data.datasets.length > 0;
  const [firstDataset] = data.datasets;

  return {
    type: 'pie',
    options: {
      ...buildSharedChrome(theme, buildOptions.showToolbox === true),
      tooltip: {
        ...buildTooltipStyle(theme),
        trigger: 'item',
        formatter: (params: unknown): string => {
          if (typeof params !== 'object' || params === null) {
            return '';
          }
          const name = 'name' in params ? String(params.name) : '';
          const seriesName =
            'seriesName' in params &&
            (typeof params.seriesName === 'string' ||
              typeof params.seriesName === 'number')
              ? String(params.seriesName)
              : '';
          const rawValue = 'value' in params ? params.value : '';
          const value =
            typeof rawValue === 'number' || typeof rawValue === 'string'
              ? formatChartTooltipValue(rawValue)
              : '';
          const label = seriesName ? `${seriesName}: ${value}` : value;
          return name ? `${name}<br/>${label}` : label;
        },
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: { color: theme.mutedColor },
      },
      series: [
        {
          name: hasDataset ? firstDataset.name : 'Data',
          type: 'pie',
          radius: '58%',
          center: ['50%', '44%'],
          data: data.labels.map((label, index) => ({
            name: label,
            // Pie slices cannot be gaps — coerce nulls from multi-series
            // chart data to 0.
            value: hasDataset ? (firstDataset.data[index] ?? 0) : 0,
            itemStyle: { color: pickColor(palette, index) },
          })),
          emphasis: {
            scale: true,
            itemStyle: { shadowBlur: 10, shadowOffsetX: 0 },
          },
        },
      ],
    },
  };
}

/**
 * Generate an ECharts configuration for a donut chart (pie with a hollow center).
 *
 * The center label shows the sum of the first dataset when present.
 *
 * @param data The input series and labels. {@link ChartData}
 * @param config Chart configuration. {@link ChartConfig}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A {@link SupportedChartConfiguration} with `type: 'donut'`.
 * @example
 * const opt = createDonutChartOptions(data, { type: 'donut' });
 */
export function createDonutChartOptions(
  data: ChartData,
  config: ChartConfig,
  buildOptions: ChartOptionBuildOptions = {}
): SupportedChartConfiguration {
  const pie = createPieChartOptions(data, config, buildOptions);
  const hasDataset = data.datasets.length > 0;
  const total = hasDataset
    ? data.datasets[0].data.reduce<number>(
        (sum, value) => sum + (value ?? 0),
        0
      )
    : 0;
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const firstDatasetName = hasDataset ? data.datasets[0].name : 'Data';
  const sliceData = data.labels.map((label, index) => ({
    name: label,
    value: hasDataset ? (data.datasets[0].data[index] ?? 0) : 0,
    itemStyle: {
      color: pickColor(resolvePalette(config, theme), index),
    },
  }));

  const donutSeries: PieSeriesOption[] = [
    {
      name: firstDatasetName,
      type: 'pie',
      radius: ['42%', '62%'],
      center: ['50%', '44%'],
      data: sliceData,
      label: {
        show: true,
        position: 'center',
        formatter: () => `${total}`,
        fontSize: 22,
        fontWeight: 600,
        color: theme.textColor,
      },
      labelLine: { show: false },
      emphasis: {
        scale: true,
        label: { show: true },
      },
    },
  ];

  return {
    type: 'donut',
    options: {
      ...pie.options,
      series: donutSeries,
    },
  };
}
